/**
 * QuestionnaireModal Component (T067-T078)
 *
 * Modal for displaying and completing questionnaires.
 * Fetches questions, handles user answers, submits responses, and shows profile generation status.
 */

import React, { useState, useEffect } from 'react'
import { questionnaireService } from '@/services/questionnaireService'
import { ProfileView } from './ProfileView'
import styles from './QuestionnaireModal.module.css'

interface Question {
  id: number
  order: number
  text: string
  question_type: string
  options: any
  is_required: boolean
}

interface Questionnaire {
  id: number
  category: string
  title: string
  description: string
  questions: Question[]
  question_count: number
}

interface QuestionnaireModalProps {
  isOpen: boolean
  onClose: () => void
  questionnaireId: number
  conversationId: number
}

export const QuestionnaireModal: React.FC<QuestionnaireModalProps> = ({
  isOpen,
  onClose,
  questionnaireId,
  conversationId
}) => {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [responseId, setResponseId] = useState<number | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // T070: Fetch questionnaire on modal open
  useEffect(() => {
    if (isOpen && questionnaireId) {
      fetchQuestionnaire()
    }
  }, [isOpen, questionnaireId])

  const fetchQuestionnaire = async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await questionnaireService.getQuestionnaire(questionnaireId)
      setQuestionnaire(data)
    } catch (err: any) {
      console.error('Error fetching questionnaire:', err)
      setError(err.response?.data?.error || 'Failed to load questionnaire')
    } finally {
      setLoading(false)
    }
  }

  // Handle answer change
  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId.toString()]: value
    }))

    // Clear validation error for this question
    setValidationErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[questionId.toString()]
      return newErrors
    })
  }

  // T072: Validate form before submission
  const validateAnswers = (): boolean => {
    if (!questionnaire) return false

    const errors: Record<string, string> = {}
    let isValid = true

    questionnaire.questions.forEach(question => {
      if (question.is_required) {
        const answer = answers[question.id.toString()]
        if (!answer || answer.trim() === '') {
          errors[question.id.toString()] = 'This question is required'
          isValid = false
        }
      }
    })

    setValidationErrors(errors)
    return isValid
  }

  // T073: Handle submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate
    if (!validateAnswers()) {
      setError('Please answer all required questions')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // Submit response
      const response = await questionnaireService.submitResponse(
        questionnaireId,
        conversationId,
        answers
      )

      setResponseId(response.id)
      setSubmitted(true)

      // T075: Success feedback will be shown by ProfileView component
    } catch (err: any) {
      console.error('Error submitting questionnaire:', err)
      setError(err.response?.data?.error || 'Failed to submit questionnaire')
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    // Reset state
    setQuestionnaire(null)
    setAnswers({})
    setError(null)
    setSubmitted(false)
    setResponseId(null)
    setValidationErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className={styles.modalOverlay}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="questionnaire-modal-title"
    >
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close questionnaire"
        >
          ×
        </button>

        {/* T074: Loading state */}
        {loading && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading questionnaire...</p>
          </div>
        )}

        {/* T076: Error state */}
        {error && !loading && !submitted && (
          <div className={styles.errorContainer} role="alert">
            <p className={styles.errorMessage}>{error}</p>
            <button
              className={styles.retryButton}
              onClick={fetchQuestionnaire}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Show profile view after submission */}
        {submitted && responseId && (
          <ProfileView
            responseId={responseId}
            onClose={handleClose}
          />
        )}

        {/* T071: Render questionnaire form */}
        {!loading && !error && !submitted && questionnaire && (
          <div className={styles.questionnaireContainer}>
            <div className={styles.header}>
              <h2 id="questionnaire-modal-title" className={styles.title}>
                {questionnaire.title}
              </h2>
              {questionnaire.description && (
                <p className={styles.description}>{questionnaire.description}</p>
              )}
              <div className={styles.progressIndicator}>
                Question {Object.keys(answers).length} of {questionnaire.question_count}
              </div>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.questionsContainer}>
                {questionnaire.questions.map((question) => (
                  <div
                    key={question.id}
                    className={styles.questionCard}
                    role="group"
                    aria-labelledby={`question-${question.id}`}
                  >
                    <label
                      id={`question-${question.id}`}
                      htmlFor={`answer-${question.id}`}
                      className={styles.questionText}
                    >
                      {question.order}. {question.text}
                      {question.is_required && (
                        <span className={styles.required} aria-label="required">
                          *
                        </span>
                      )}
                    </label>

                    {/* Conditional rendering based on question type */}
                    {question.question_type === 'scale' ? (
                      <div className={styles.scaleOptions}>
                        {[1, 2, 3, 4, 5].map(value => (
                          <button
                            key={value}
                            type="button"
                            className={`${styles.scaleButton} ${
                              answers[question.id.toString()] === value.toString() ? styles.selected : ''
                            }`}
                            onClick={() => handleAnswerChange(question.id, value.toString())}
                            aria-label={`Select option ${value}`}
                          >
                            <span className={styles.scaleNumber}>{value}</span>
                            <span className={styles.scaleLabel}>
                              {value === 1 && 'Very Rarely'}
                              {value === 2 && 'Sometimes'}
                              {value === 3 && 'Often'}
                              {value === 4 && 'Very Often'}
                              {value === 5 && 'Always'}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <textarea
                        id={`answer-${question.id}`}
                        className={`${styles.answerInput} ${
                          validationErrors[question.id.toString()] ? styles.error : ''
                        }`}
                        value={answers[question.id.toString()] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder="Type your answer here..."
                        rows={4}
                        aria-required={question.is_required}
                        aria-invalid={!!validationErrors[question.id.toString()]}
                        aria-describedby={
                          validationErrors[question.id.toString()]
                            ? `error-${question.id}`
                            : undefined
                        }
                      />
                    )}
                    {validationErrors[question.id.toString()] && (
                      <p
                        id={`error-${question.id}`}
                        className={styles.validationError}
                        role="alert"
                      >
                        {validationErrors[question.id.toString()]}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={submitting}
                  aria-label="Submit questionnaire answers"
                >
                  {submitting ? (
                    <>
                      <span className={styles.spinner}></span>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    'Submit Answers'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
